import { VideoPlayer } from 'app/pages/video/videoPlayer';
import { Form, Formik } from 'formik';
import { useSingleVideoQuery } from 'lib/api/videos/useSingleVideoQuery';
import { useVideoUploadMutation } from 'lib/api/youtube/useVideoUploadMutation';
import { LoadingIndicator, Logo } from 'lib/components';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import { FormikLabel } from 'lib/components/formik/FormikLabel';
import { FormikRadioField } from 'lib/components/formik/FormikRadioField';
import { FormikTextAreaField } from 'lib/components/formik/FormikTextAreaField';
import { Flex } from 'lib/components/styles/layout';
import { Heading, ParagraphNormal } from 'lib/components/styles/typography';
import { errorToast } from 'lib/components/toasts/error';
import { useLogoUrl } from 'lib/hooks/useLogoUrl';
import queryString from 'query-string';
import React from 'react';
import { Button } from 'react-covideo-common';
import { useTranslation } from 'react-i18next';
import { IoMdSave } from 'react-icons/io';
import { useHistory, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components/macro';
import * as Yup from 'yup';

interface YoutubeUploadValues {
  title: string;
  description?: string;
  privacyStatus: 'public' | 'private' | 'unlisted';
}

const getValidationSchema = (t: (key: string) => string) =>
  Yup.object({
    title: Yup.string()
      .required(t('Title is required'))
      .max(100, t('Title must be 100 characters or less')),
    privacyStatus: Yup.string()
      .oneOf(['public', 'private', 'unlisted'])
      .required(t('Privacy setting is required')),
  });

export const Main = () => {
  const { t } = useTranslation();
  const themes = useTheme();
  const location = useLocation();
  const history = useHistory();
  const queryParams = queryString.parse(location.search);
  const state = queryParams.state as string;
  // const code = queryParams.code as string;
  const { logoUrl } = useLogoUrl('dark');

  const parsedState = JSON.parse(state || '{}');
  const videoId = parsedState.videoId;
  const { data: video, isLoading: isVideoLoading } = useSingleVideoQuery({
    videoId: videoId ? parseInt(videoId, 10) : 0,
    enabled: !!videoId,
  });

  const { mutateAsync: uploadVideoMutation, isLoading: isVideoUploading } =
    useVideoUploadMutation();

  const initialValues: YoutubeUploadValues = {
    title: video?.title || '',
    description: video?.description || '',
    privacyStatus: 'public',
  };
  const loading = isVideoLoading || isVideoUploading;

  const handleSubmit = async (values: YoutubeUploadValues) => {
    if (!videoId) {
      errorToast({ title: t('Invalid Video') });
      return;
    }
    await uploadVideoMutation({
      title: values.title,
      description: values.description || '',
      privacyStatus: values.privacyStatus,
      videoId: parseInt(videoId, 10),
      code: queryParams.code as string,
    });
  };
  // Show loading while fetching video
  if (isVideoLoading) {
    return (
      <Flex
        flexDirection='column'
        alignItems='center'
        padding='24px'
        maxWidth='700px'
      >
        <LoadingIndicator isLoading={true} height='400px' />
      </Flex>
    );
  }

  if (!videoId || (!video && !isVideoLoading)) {
    return (
      <Flex
        alignItems='center'
        padding='24px'
        maxWidth='700px'
        margin='0 auto'
        width='100%'
      >
        <Flex
          justifyContent='center'
          flexDirection='row'
          margin='0 0 24px 0'
          alignItems='center'
        >
          <Logo src={logoUrl} alt={t('Logo')} />
        </Flex>
        <Flex justifyContent='center' flexDirection='row'>
          <Heading>{t('Upload to YouTube')}</Heading>
        </Flex>
        <ParagraphNormal color={themes.colors.danger[100]}>
          {t('Invalid Video')}
        </ParagraphNormal>
        <Button
          text={t('Go Back to Home')}
          variant='primary'
          onClick={() => history.push('/home')}
        />
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection='column'
      alignItems='center'
      padding='24px'
      maxWidth='700px'
      margin='0 auto'
      width='100%'
    >
      <Flex
        justifyContent='center'
        flexDirection='row'
        margin='0 0 24px 0'
        alignItems='center'
      >
        <Logo src={logoUrl} alt={t('Logo')} />
      </Flex>
      <Flex justifyContent='center' flexDirection='row'>
        <Heading>{t('Upload to YouTube')}</Heading>
      </Flex>
      {video && (
        <Flex width='100%' maxWidth='700px' margin='24px 0' borderRadius='8px'>
          <VideoPlayer
            playerBackgroundColor={video.playerBackgroundColor}
            playButtonPosition={video.playButtonPosition}
            playerIconsColor={video.playerIconsAndTextColor}
            videoSource={video.videoSource}
            videoRef={React.createRef()}
            videoId={video.id.toString()}
          />
        </Flex>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema(t)}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, isValid }) => (
          <Form>
            <Flex gap='12px' margin='12px 0' width='700px'>
              <FormikInputField
                name='title'
                label={t('Title')}
                isRequired={true}
                placeholder={t('Enter video title')}
                required
                disabled={isSubmitting || loading}
              />
              <FormikTextAreaField
                name='description'
                label={t('Description')}
                isRequired={false}
                placeholder={t('Enter video description (optional)')}
                disabled={isSubmitting || loading}
                maxLength={5000}
              />
              <Flex gap='12px'>
                <FormikLabel label={t('Privacy')} isRequired={true} />
                <FormikRadioField
                  name='privacyStatus'
                  label={t('Public')}
                  value='public'
                  readonly={isSubmitting || loading}
                />
                <FormikRadioField
                  name='privacyStatus'
                  label={t('Private')}
                  value='private'
                  readonly={isSubmitting || loading}
                />
              </Flex>
              <Button
                text={
                  isSubmitting || loading
                    ? t('Uploading...')
                    : t('Upload to YouTube')
                }
                extendStyles={{
                  container: {
                    marginTop: 24,
                  },
                }}
                type='submit'
                disabled={isSubmitting || loading || !isValid}
                loading={isSubmitting || loading}
                variant='primary'
                icon={<IoMdSave />}
              />
            </Flex>
          </Form>
        )}
      </Formik>
    </Flex>
  );
};
